import { request } from "utils/request"
import { asyncAction, removeFromMap } from "./common"
import { loading, analyses, message, sampleView } from "./"
import i18n from "i18n"
import { getState } from "storeAccess"

const initialState = {}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "samples/fetchForPlanned/BEGIN":
    case "samples/fetchForUnplanned/BEGIN":
      return { ...state, [action.args[0]]: { isLoading: true } }
    case "samples/fetchForPlanned":
    case "samples/fetchForUnplanned":
      return { ...state, [action.res.analysisId]: { isLoading: false, items: action.res.items } }
    case "iap/runFillInSamples": // We need to refresh all the samples on analyses, when running fill-in-samples IAP
    case "samples/changePriority": // Reset sample state when changing priority to prevent outdated sample-info
    case "samples/clear":
    case "auth/logout":
    case 'samples/flag-sample':
    case 'samples/unflag-sample':
      return initialState
    case "samples/delete-unlocked":
    case "samples/fail-samples":
    case "samples/assign-samples":
      return state
    case "samples/unplan-samples":
      // When unplanning samples, also clear the samples state for that analysis
      // This ensures the component re-fetches if the analysis still exists
      if (action.args && action.args[0]) {
        return removeFromMap(state, { id: action.args[0] })
      }
      return state
    case "analysisTasks/move": // When moving a task, the sample status may have changed
      return removeFromMap(state, { id: action.res.analysisId })
    case "samples/create":
      // When creating samples, the API returns sampleGroups and samples.
      // Map returned sampleIds to analysisIds using sampleGroups and insert
      // the sample observations into the relevant analysis entry in the samples state.
      try {
        const res = action.res || {}
        const groups = res.sampleGroups || []
        const samples = res.samples || []
        if (groups.length === 0 || samples.length === 0) return state

        const sampleIdToAnalysis = {}
        groups.forEach((group) => {
          ;(group.analyses || []).forEach((a) => {
            if (a && a.sampleId) sampleIdToAnalysis[a.sampleId] = a.analysisId
          })
        })

        let newState = { ...state }
        samples.forEach((s) => {
          const analysisId = sampleIdToAnalysis[s.id]
          if (!analysisId) return
          const existing = (newState[analysisId] && newState[analysisId].items) ? newState[analysisId].items.slice() : []
          // avoid duplicates
          if (!existing.find((e) => String(e.id) === String(s.id))) {
            existing.push(s)
            newState[analysisId] = { isLoading: false, items: existing }
          }
        })
        return newState
      } catch {
        return state
      }
    default:
      return state
  }
}

// Fetch only for one analysis at a time
export const fetchForPlanned = asyncAction("samples/fetchForPlanned", (dispatch, analysisId) => {
  return request("GET", `/analysis/${analysisId}/samples`).then((res) => ({ analysisId, items: res.items }))
})

export const fetchForUnplanned = asyncAction("samples/fetchForUnplanned", (dispatch, analysisId) => {
  return request("GET", `/analysis/unplanned/${analysisId}/samples`).then((res) => ({ analysisId, items: res.items }))
})

export const removeAllUnplanned = asyncAction("samples/delete-unlocked", (dispatch) => {
  return request("DELETE", `/samples/delete-unlocked`).then((res) => {
    // fetching the updated data which will now be empty unless it's planned
    dispatch(loading.fetchAll())
    return res
  })
})

// SPEC-032: fail the selected samples → the backend (DELETE /samples/fail-samples) keeps a FAILED
// copy and flips the originals to RERUN, reallocating them back into the unplanned pool. Restored
// for non-LIMS teams (LIMS teams fail via the QC Results & Approvals / OOS flow). The trailing
// params are accepted (and ignored) so the Sample View call site can pass its sample metadata.
export const failSamples = asyncAction(
  "samples/fail-samples",
  (dispatch, analysisId, sampleIds, _isSampleView = false, _sampleName = "", _productName = "", _observationId = "") => {
    return request("DELETE", `/samples/fail-samples`, { patch: { ids: sampleIds } }).then((res) => {
      dispatch(analyses.fetchUnplanned())
      dispatch(analyses.fetchPlanned()) // keep task durations in sync when editing in AnalysisTaskConfig
      dispatch(fetchForPlanned(analysisId))
      return res
    })
  }
)

// The fourth parameter 'samples' is only used in plannableSamples.js
export const unplanSamples = asyncAction("samples/unplan-samples", (dispatch, analysisId, sampleIds, _samples) => {
  return request("PATCH", `/samples/unplan-samples`, { patch: { ids: sampleIds } }).then((res) => {
    dispatch(analyses.fetchUnplanned())
    return dispatch(analyses.fetchPlanned()).then((fetchedData) => {
      // Check if the analysis still exists in the fetched data (not in state, since state merges old data)
      const analysisStillExists = fetchedData.analyses.some(analysis => analysis.id === analysisId)
      if (!analysisStillExists) {
        // Analysis was deleted on backend (all samples were unplanned), remove it from local state
        dispatch({ type: "analyses/remove", res: { id: analysisId } })
      } else {
        // Analysis still exists, fetch updated samples
        dispatch(fetchForPlanned(analysisId))
      }
      return res
    })
  })
})

// New: create a single sample by posting to /samples
export const createSample = asyncAction("samples/create", (dispatch, input) => {
  return request("POST", "/samples", { input }).then((res) => {
    // Refresh sample view and analyses to reflect the newly created sample
    try {
      // If the API returned sampleGroups/samples, merge them into the current sampleView state
      // to avoid fetching the entire sample view again. Otherwise fall back to fetching.
      const state = getState()
      if (res && (res.sampleGroups || res.samples)) {
        const current = state.sampleView.sampleGroups || []
        const newGroups = res.sampleGroups || []

        const merged = current.slice()
        newGroups.forEach((ng) => {
          const idx = merged.findIndex((s) => s.sampleName === ng.sampleName && String(s.observationId) === String(ng.observationId))
          if (idx >= 0) {
            const existing = merged[idx]
            // merge analyses arrays without duplicating by sampleId
            const existingAnalyses = existing.analyses || []
            const newAnalyses = ng.analyses || []
            const mergedAnalyses = existingAnalyses.slice()
            newAnalyses.forEach((na) => {
              if (!mergedAnalyses.find((e) => String(e.sampleId) === String(na.sampleId))) mergedAnalyses.push(na)
            })
            merged[idx] = { ...existing, ...ng, analyses: mergedAnalyses }
          } else {
            merged.push(ng)
          }
        })

        const resForSampleView = {
          items: merged,
          should_be_done_samples: state.sampleView.shouldBeDoneSamples || [],
          product_analysis_relationships: state.sampleView.productAnalysis || {},
        }

        // Dispatch a synthetic sampleView/fetch action with merged items so reducer will rebuild sorted groups
        dispatch({ type: "sampleView/fetch", res: resForSampleView })
      } else {
        dispatch(sampleView.fetch())
      }

      dispatch(analyses.fetchUnplanned())
    } catch {
      // ignore
    }
    if (res && res.samples) {
      dispatch(message.success(i18n.t("adhoc.sampleCreated", { ns: "messages" })))
      return res.samples
    }
    if (res && res.sample) {
      dispatch(message.success(i18n.t("adhoc.sampleCreated", { ns: "messages" })))
      return res.sample
    }
    return res
  })
})

export const assignSamples = asyncAction("samples/assign-samples", (dispatch, analysisId, sampleIds) => {
  return request("PATCH", `/samples/assign-samples/${analysisId}`, { patch: { ids: sampleIds } }).then((res) => {
    dispatch(analyses.fetchUnplanned())
    dispatch(analyses.fetchPlanned())
    dispatch(fetchForPlanned(analysisId))

    // If the backend returns a list of deleted analysis IDs, remove them from state
    if (res && res.deleted_analysis_ids && res.deleted_analysis_ids.length > 0) {
      res.deleted_analysis_ids.forEach(deletedId => {
        dispatch({ type: "analyses/remove", res: { id: deletedId } })
      })
    }

    if (res && res.message) {
      dispatch(message.warning(res.message))
    }
    return res
  })
})

export function clear() {
  return { type: "samples/clear" }
}

export const runSampleAllocation = asyncAction("teamStatus/runSampleAllocation", async (dispatch) => {
  return await request("POST", "/samples/run-sample-allocation").then(async () => {
    await dispatch(analyses.fetchUnplanned())
  })
})

export const changePriority = asyncAction("samples/changePriority", (dispatch, newPriority, newSampleName, observationId) => {
  const sortingParameter = getState().teamStatus.sortingParameter
  return request("PATCH", `/samples/change-priority`, { patch: { priority: newPriority, sampleName: newSampleName, observationId: observationId } }).then((res) => {
    res.sortingParameter = sortingParameter
    return res
  })
})

export const flagSample = asyncAction("samples/flag-sample", (dispatch, analysisId, sampleName) => {
  return request("PATCH", `/sample/${analysisId}`, { patch: { isFlagged: true } }).then(() => {
    dispatch({
      type: "samples/flag-sample",
      payload: { sampleName, analysisId },
    });
  });
});


export const unflagSample = asyncAction("samples/unflag-sample", (dispatch, analysisId, sampleName) => {
  return request("PATCH", `/sample/${analysisId}`, { patch: { isFlagged: false } }).then(() => {
    dispatch({
      type: "samples/unflag-sample",
      payload: { sampleName, analysisId },
    });
  });
});

